// Veranstaltungstexte pflegen (DCP-2228 Folge-Feature, erweitert durch US-203/US-204)
//
// Seit PRD-020 pflegen auch Depot-Rollen hier — jede aber nur die Vorlagen ihres Partners.
// Vorlagen der Zentrale erscheinen in der Liste (der VA-Dialog braucht sie), lassen sich
// von einer Depot-Rolle aber nicht ändern: ihre Zeile ist gesperrt statt versteckt, sonst
// wäre nicht erkennbar, dass es sie gibt.
//
// Vorlagen für den VA-Beschreibungstext. Aufbau wie MailingListPage, mit einem
// Unterschied im Löschdialog: dort wird gezählt, wie viele Veranstaltungen auf die
// Vorlage verweisen — und ausdrücklich dazugesagt, dass ihr Text erhalten bleibt. Er
// wurde beim Einsetzen kopiert; nur der Verweis entfällt. Ohne diesen Satz liest sich
// „3 Veranstaltungen betroffen" wie ein Datenverlust.
//
// ⚠️ Wie bei den Mailings lässt sich die Verweigerung NICHT an der Antwort ablesen:
// GET /api/event-texts ist absichtlich für jede Rolle offen (der VA-Dialog liest es).
// Die Rolle muss vor dem Rendern selbst geprüft werden.

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowLeft, Pencil, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { useToast } from '@/hooks/use-toast';
import { useMe } from '@/hooks/usePermissions';
import { ApiError, apiClient } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import {
  useEventTexts,
  useSaveEventText,
  useDeleteEventTexts,
} from '@/services/event-service';
import { formatDay } from '@/features/events/mailing-match';
import { Role, type EventText, type SaveEventTextRequest } from '@/types';
import { EventTextFormDialog } from './EventTextFormDialog';
import { pruneSelection, selectionState, toggleAll, toggleOne } from './selection';

/** Textauszug ohne Auszeichnung, für die Übersichtsspalte. */
function excerpt(html: string, max = 90): string {
  const plain = html
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  return plain.length > max ? `${plain.slice(0, max)}…` : plain;
}

export function EventTextListPage() {
  const { t } = useTranslation('settings');
  const { toast } = useToast();

  const { data: me } = useMe();
  /**
   * Darf diese Person die Pflegeseite überhaupt benutzen?
   *
   * Seit US-203 am RECHT, nicht an der Rolle: `eventTexts.edit` lässt sich einem einzelnen
   * Nutzer entziehen, obwohl seine Rolle es gewährt. Vorher hing die Seite an
   * `Zentral || Admin` und sperrte damit jeden Partner aus.
   *
   * ⚠️ Der Server prüft dasselbe Recht (requireEventTextsEdit). Das hier blendet nur aus,
   * es sichert nichts ab.
   */
  const canManage = (me?.permissions?.eventTexts ?? []).includes('edit');
  /**
   * Gehört diese Vorlage dem Anfragenden — darf er sie also ändern?
   *
   * Spiegelt canEditTemplate im Backend. Zentrale und Admin dürfen alles; eine Depot-Rolle
   * nur, was zu ihrem Partner gehört. Vorlagen der Zentrale (ohne Partnernummer) darf sie
   * verwenden, aber nicht anfassen.
   */
  const isCentralRole = me?.role === Role.Zentral || me?.role === Role.Admin;
  const canEdit = (text: EventText) =>
    isCentralRole ? true : Boolean(text.ownerPartnerKey) && text.ownerPartnerKey === me?.partnerKey;

  const { data: texts = [], isLoading, isError, error } = useEventTexts();
  const saveMutation = useSaveEventText();
  const deleteMutation = useDeleteEventTexts();

  const [selected, setSelected] = useState<string[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<EventText | null>(null);
  // Erzwingt einen frischen Mount von EventTextFormDialog bei jedem Öffnen (29.09.2026 —
  // Fix für stehenbleibenden Text zwischen zwei "Neu"-Aufrufen): eine reine
  // React-State-Änderung wie setEditing(null) reicht nicht aus, wenn `editing` davor schon
  // null war, da sich dann keine Prop ändert.
  const [dialogInstance, setDialogInstance] = useState(0);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string[] | null>(null);
  const [usage, setUsage] = useState<{ count: number; available: boolean } | null>(null);

  // Nur die änderbaren: die Massenauswahl darf nichts anbieten, was der Server danach mit
  // 403 abweist. Der Löschendpunkt verwirft den GANZEN Stapel, wenn eine fremde Vorlage
  // darin steckt — eine mitgewählte Vorlage der Zentrale würde also auch das Löschen der
  // eigenen verhindern.
  const ids = useMemo(() => texts.filter(canEdit).map((text) => text.id), [texts]);

  // Gegen die frisch geladene Liste schneiden: nach einem Löschen kann die Auswahl
  // Kennungen enthalten, die es nicht mehr gibt.
  const liveSelection = useMemo(() => pruneSelection(selected, ids), [selected, ids]);

  const headerState = selectionState(texts.length, liveSelection.length);
  const isDenied =
    error instanceof ApiError && (error.status === 401 || error.status === 403);

  // Verweise erst zählen, wenn der Dialog aufgeht — nicht für jede Zeile im Voraus.
  useEffect(() => {
    if (!confirmDelete) {
      setUsage(null);
      return;
    }

    let abgebrochen = false;
    setUsage(null);

    apiClient
      .getEventTextUsage(confirmDelete)
      .then((antwort) => {
        if (abgebrochen) return;
        const summe = Object.values(antwort.usage).reduce((a, b) => a + b, 0);
        setUsage({ count: summe, available: antwort.available });
      })
      // Ein Fehlschlag darf das Löschen nicht verhindern: die Warnung ist eine
      // Information, keine Sperre.
      .catch(() => {
        if (!abgebrochen) setUsage({ count: 0, available: false });
      });

    return () => {
      abgebrochen = true;
    };
  }, [confirmDelete]);

  const openCreate = () => {
    setEditing(null);
    setSaveError(null);
    setDialogInstance((n) => n + 1);
    setDialogOpen(true);
  };

  const openEdit = (text: EventText) => {
    setEditing(text);
    setSaveError(null);
    setDialogInstance((n) => n + 1);
    setDialogOpen(true);
  };

  const handleSave = (req: SaveEventTextRequest) => {
    setSaveError(null);
    saveMutation.mutate(req, {
      onSuccess: () => {
        toast({ title: t('eventTexts.toast.saved') });
        setDialogOpen(false);
      },
      // Serverfehler bleiben IM Dialog. Bei der Sperre ist das entscheidend: der eigene
      // Text steht noch im Editor und muss kopierbar bleiben. Ein Toast plus
      // geschlossener Dialog wäre genau der Verlust, den die Sperre verhindern soll.
      onError: (e) => setSaveError(e instanceof Error ? e.message : String(e)),
    });
  };

  const handleDelete = (doomed: string[]) => {
    deleteMutation.mutate(doomed, {
      onSuccess: (result) => {
        setConfirmDelete(null);
        setSelected([]);

        toast({ title: t('eventTexts.toast.deleted', { count: result.removed.length }) });

        if (result.failed.length > 0) {
          toast({
            title: t('eventTexts.toast.deleteFailed'),
            description: result.failed.map((f) => `${f.id}: ${f.reason}`).join('\n'),
            variant: 'destructive',
          });
        }
      },
      onError: (e) =>
        toast({
          title: t('eventTexts.toast.error'),
          description: e instanceof Error ? e.message : String(e),
          variant: 'destructive',
        }),
    });
  };

  if (!canManage) {
    return (
      <div className="space-y-6">
        <h2 className="text-3xl font-bold tracking-tight">{t('eventTexts.page.title')}</h2>
        <p className="p-8 text-center text-muted-foreground">
          {t('eventTexts.page.noAccess')}
        </p>
      </div>
    );
  }

  const titleOf = (id: string) => texts.find((text) => text.id === id)?.title ?? id;

  /** Beschreibung im Löschdialog, inklusive Verweis-Hinweis. */
  const deleteDescription = (): string => {
    if (!confirmDelete) return '';

    const kopf =
      confirmDelete.length === 1
        ? t('eventTexts.delete.descriptionOne', { title: titleOf(confirmDelete[0]) })
        : t('eventTexts.delete.descriptionMany', { count: confirmDelete.length });

    if (!usage) return kopf;
    if (!usage.available) return `${kopf}\n\n${t('eventTexts.delete.usageUnavailable')}`;
    if (usage.count === 0) return kopf;

    return `${kopf}\n\n${t('eventTexts.delete.usageHint', { count: usage.count })}`;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <Button asChild variant="ghost" size="sm" className="-ml-2 h-7 gap-1 px-2">
            <Link to="/settings">
              <ArrowLeft className="h-3.5 w-3.5" />
              {t('eventTexts.page.backToSettings')}
            </Link>
          </Button>
          <h2 className="text-3xl font-bold tracking-tight">{t('eventTexts.page.title')}</h2>
          <p className="text-muted-foreground">{t('eventTexts.page.description')}</p>
        </div>
        <Button className="gap-2" onClick={openCreate}>
          <Plus className="h-4 w-4" />
          {t('eventTexts.actions.create')}
        </Button>
      </div>

      {isLoading ? (
        <p className="p-8 text-center text-muted-foreground">{t('eventTexts.page.loading')}</p>
      ) : isDenied ? (
        <p className="p-8 text-center text-muted-foreground">{t('eventTexts.page.noAccess')}</p>
      ) : isError ? (
        <p className="p-8 text-center text-red-600">{t('eventTexts.page.loadError')}</p>
      ) : texts.length === 0 ? (
        <p className="rounded-md border p-8 text-center text-muted-foreground">
          {t('eventTexts.page.empty')}
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">
                <Checkbox
                  checked={headerState === 'all'}
                  onCheckedChange={() => setSelected(toggleAll(ids, liveSelection))}
                  aria-label={t('eventTexts.table.selectAll')}
                  className={cn(headerState === 'some' && 'data-[state=checked]:bg-primary/50')}
                />
              </TableHead>
              <TableHead>{t('eventTexts.table.category')}</TableHead>
              <TableHead>{t('eventTexts.table.title')}</TableHead>
              <TableHead>{t('eventTexts.table.preview')}</TableHead>
              <TableHead>{t('eventTexts.table.modified')}</TableHead>
              <TableHead className="text-right">{t('eventTexts.table.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {texts.map((text) => {
              const isSelected = liveSelection.includes(text.id);
              const mine = canEdit(text);

              return (
                <TableRow key={text.id} className={cn(isSelected && 'bg-blue-50')}>
                  <TableCell>
                    <Checkbox
                      checked={isSelected}
                      disabled={!mine}
                      onCheckedChange={() => setSelected(toggleOne(liveSelection, text.id))}
                      aria-label={t('eventTexts.table.selectRow', { title: text.title })}
                    />
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">{text.category}</Badge>
                  </TableCell>
                  <TableCell className="font-medium">{text.title}</TableCell>
                  {/* Auszug ohne Auszeichnung: rohes HTML in der Tabelle wäre unlesbar,
                      und es zu rendern würde die Spaltenbreite sprengen. */}
                  <TableCell className="max-w-[28rem] text-sm text-muted-foreground">
                    {excerpt(text.text)}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {formatDay(text.modifiedAt)}
                    {text.modifiedBy && <div className="truncate">{text.modifiedBy}</div>}
                  </TableCell>
                  <TableCell className="text-right">
                    {/* Gesperrt statt ausgeblendet, mit Begründung im Titel: eine
                        verschwundene Schaltfläche liest sich wie ein Fehler. */}
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={!mine}
                      onClick={() => openEdit(text)}
                      aria-label={t('eventTexts.actions.edit')}
                      title={mine ? undefined : t('eventTexts.actions.foreign')}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={!mine}
                      onClick={() => setConfirmDelete([text.id])}
                      aria-label={t('eventTexts.actions.delete')}
                      title={mine ? undefined : t('eventTexts.actions.foreign')}
                    >
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {liveSelection.length > 0 && (
        <div className="sticky bottom-0 z-40 flex items-center justify-between gap-4 border-t bg-background/95 p-3 backdrop-blur">
          <span className="text-sm text-muted-foreground">
            {t('eventTexts.bulk.selected', { count: liveSelection.length })}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setSelected([])}>
              {t('eventTexts.bulk.clearSelection')}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              className="gap-2"
              onClick={() => setConfirmDelete([...liveSelection])}
            >
              <Trash2 className="h-4 w-4" />
              {t('eventTexts.bulk.deleteSelected')}
            </Button>
          </div>
        </div>
      )}

      <EventTextFormDialog
        key={dialogInstance}
        open={dialogOpen}
        eventText={editing}
        allTexts={texts}
        saveError={saveError}
        onOpenChange={setDialogOpen}
        onSave={handleSave}
        isSaving={saveMutation.isPending}
      />

      <ConfirmationDialog
        open={confirmDelete !== null}
        onOpenChange={(open) => !open && setConfirmDelete(null)}
        title={t(
          confirmDelete?.length === 1
            ? 'eventTexts.delete.title'
            : 'eventTexts.delete.titleMany'
        )}
        description={deleteDescription()}
        confirmLabel={t('eventTexts.delete.confirm')}
        cancelLabel={t('eventTexts.form.cancel')}
        variant="destructive"
        onConfirm={() => confirmDelete && handleDelete(confirmDelete)}
      />
    </div>
  );
}
